import {Directive, ElementRef, HostListener, inject, input, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, skip} from 'rxjs/operators';
import {ImportService} from '../services/import/import.service';
import {DialogService} from '../services/dialog/dialog.service';
import {ImportLocationEnum} from '../enums/import-location.enum';
import {CheckedOutDocumentService} from '../services/checked-out-document/checked-out-document.service';
import {Magnet} from '../bfa-api/models/magnet';
import {outputFromObservable} from '@angular/core/rxjs-interop';
import {AppDropImportTargetType} from '../types/app-drop-import-target.type';

@Directive({
    selector: '[appDropImport]',
    standalone: true,
})
export class DropImportDirective implements OnInit, OnDestroy {
    public appDropImport = input<ImportLocationEnum | Magnet | '' | undefined | null>(undefined);
    public dropImportVaultId = input<string | undefined>(undefined);
    public droppedEmitter = new Subject<{
        files: Array<File>;
        target: AppDropImportTargetType;
    }>();
    public readonly dropped = outputFromObservable(this.droppedEmitter);
    private appDropImportTarget: AppDropImportTargetType;
    private readonly dragOverClass$: BehaviorSubject<boolean>;
    private readonly subscriptions: Subscription;
    private readonly dragOverCssClassName = 'drag-over';
    private readonly dragOverDeniedCssClassName = 'drag-over-denied';
    private readonly noPointerEventsCssClassName = 'no-pointer-events';
    private readonly el = inject(ElementRef);
    private readonly importService = inject(ImportService);
    private readonly dialogService = inject(DialogService);
    private readonly checkedOutDocumentService = inject(CheckedOutDocumentService);

    public constructor() {
        this.subscriptions = new Subscription();
        this.dragOverClass$ = new BehaviorSubject<boolean>(false);
    }

    @HostListener('drop', ['$event'])
    public onFileDrop(dragEvent: DragEvent): void {
        this.dragOverClass$.next(false);

        this.el.nativeElement.classList.remove(this.dragOverCssClassName, this.dragOverDeniedCssClassName);
        dragEvent.stopPropagation();
        dragEvent.preventDefault();

        if (!dragEvent.dataTransfer) {
            return;
        }

        if (!this.dataTransferHasFiles(dragEvent.dataTransfer)) {
            this.dialogService.showDialog({
                messageTranslationKey: 'IMPORT.ERROR_FOLDERS_CURRENTLY_NOT_SUPPORTED'
            }, 'folder-import-not-supported');
            return;
        }

        const files: Array<File> = [];
        for (const element of dragEvent.dataTransfer.items) {
            const file: File | null = element.getAsFile();
            if (file) {
                files.push(file);
            }
        }

        if (files.length > 0) {
            this.appDropImportTarget = this.getAppDropImportTarget();
            if (this.droppedEmitter.observed) {
                this.droppedEmitter
                    .next({
                        files,
                        target: this.appDropImportTarget
                    });
            } else {
                this.importService.showImportDialog(files, this.dropImportVaultId(), undefined, this.appDropImportTarget);
                this.importService.checkForCheckingInDocument()
                    .then(importDocumentId => {
                        if (importDocumentId) {
                            this.checkedOutDocumentService.checkInDocument(importDocumentId)
                                .then();
                        }
                    });
            }
        }
    }

    @HostListener('dragover', ['$event'])
    public onFileDragOver(dragEvent: DragEvent): void {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();

        if (dragEvent.dataTransfer) {
            const items = dragEvent.dataTransfer.items;
            if ((items && items.length > 0 && items[0].kind === 'file') || dragEvent.dataTransfer.files.length > 0 || dragEvent.dataTransfer.types.includes('Files')) {
                dragEvent.dataTransfer.dropEffect = 'copy';
                this.dragOverClass$.next(true);
            } else {
                dragEvent.dataTransfer.dropEffect = 'none';
            }
        }
    }

    @HostListener('dragleave')
    public onFileDragLeave(): void {
        this.dragOverClass$.next(false);
    }

    public ngOnInit(): void {
        this.el.nativeElement.setAttribute('droppable', true);
        this.subscriptions.add(this.dragOverClass$.pipe(
                skip(1),
                distinctUntilChanged(),
                debounceTime(50)
            )
            .subscribe(isDraggingOver => {
                if (isDraggingOver) {
                    this.el.nativeElement.classList.add(this.dragOverCssClassName);
                    // Removing pointer events on children while dragging prevents the dragleave event to be fired every time a children is hovered.
                    // Else, the background and/or the windows tooltip seem to be flickering. => See https://amagno.atlassian.net/browse/APP-1345
                    for (const child of this.el.nativeElement.children) {
                        child.classList.add(this.noPointerEventsCssClassName);
                    }
                } else {
                    this.el.nativeElement.classList.remove(this.dragOverCssClassName, this.dragOverDeniedCssClassName);
                    for (const child of this.el.nativeElement.children) {
                        child.classList.remove(this.noPointerEventsCssClassName);
                    }
                }
            }));
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private getAppDropImportTarget(): AppDropImportTargetType {
        const appDropImport = this.appDropImport();
        if (!appDropImport) {
            return undefined;
        } else if (typeof appDropImport !== 'string') {
            if ('magnetId' in appDropImport) {
                return appDropImport;
            }
        } else if (this.appDropImport() as keyof typeof ImportLocationEnum in ImportLocationEnum) {
            return {
                id: ImportLocationEnum[appDropImport],
                name: ImportLocationEnum[appDropImport]
            };
        }

        return undefined;
    }

    private dataTransferHasFiles(dataTransfer: DataTransfer): boolean {
        if (dataTransfer.files.length === 0) {
            return false;
        }

        if (dataTransfer.items && typeof (dataTransfer.items[0].webkitGetAsEntry) === 'function') {
            return dataTransfer.items[0].webkitGetAsEntry()?.isFile || false;
        }

        return true;
    };
}
